import type { Experiment } from "@lib/contexts/AppContext";
import { PUBLIC_ROUTES } from "@lib/routePaths";
import { convertConfig } from "@root/config/convert";

// PaymentFirstCheckout variant scaffolding (U1 / OMG-1916).
//
// Gating model (decided 2026-06-16): distinct URL + Convert redirect. Convert
// redirects bucketed users to PAYMENT_FIRST_CHECKOUT_PATH; the route's presence
// IS the variant signal. On that route's mount (CheckoutPaymentFirst) we dispatch
// setExperiment. Reading the signal at
// route mount (not app mount) avoids the async window-flag timing race that the
// only prior precedent (UpsellPage's window._newCheckoutEnabled) is subject to.
//
// Session recording: Microsoft Clarity loads GLOBALLY from index.html on every
// page/arm (symmetric by construction) — the former route-level, variant-only
// init here was a runtime no-op and was removed (OMG-1921 decision 2026-07-07;
// EU consent exposure escalated on OMG-1920).

// A nested path under /checkout so it inherits, for free, every existing
// `=== "/checkout" || startsWith("/checkout/")` predicate in the app:
//   - getBasename() (App.tsx) -> public "/" basename (no auth)
//   - i18n.ts isCheckoutPath  -> skips the ~323 KB techniques namespace
//   - routes.tsx AppRoutes     -> doesn't eagerly load techniques
//   - earlyCheckoutTracking    -> fires begin_checkout pre-mount, URL-segmented
// This keeps the variant byte-for-byte equivalent to control on those paths, so
// the only intentional treatment differences are the experiment tagging + Clarity.
export const PAYMENT_FIRST_CHECKOUT_PATH = PUBLIC_ROUTES.CHECKOUT_PAYMENT_FIRST;

export const PAYMENT_FIRST_EXPERIMENT_NAME = "payment_first_checkout";
export const PAYMENT_FIRST_VARIANT_NAME = "payment_first";

// Is this pathname the variant route? The route IS the variant signal (above),
// so this is the authoritative arm test — useAnalytics calls it per event to
// tag from the URL instead of from AppContext state, which was empty on ~86% of
// variant events in production (OMG-2313; root cause unpinned, but a
// context-independent read makes the class of bug unreachable).
// Matches the path itself and anything nested under it, but NOT a sibling that
// merely shares the prefix (e.g. "/checkout/pfizer").
export const isPaymentFirstPath = (pathname: string): boolean =>
  pathname === PAYMENT_FIRST_CHECKOUT_PATH ||
  pathname.startsWith(`${PAYMENT_FIRST_CHECKOUT_PATH}/`);

// Resolve the Convert experiment payload for setExperiment from the build env.
// Non-Prod environments (UAT/dev/local) all use the UAT experiment config,
// mirroring UpsellPage's UAT-vs-Prod split.
export const getPaymentFirstExperiment = (
  env: string | undefined,
): Experiment => {
  const key = env === "Prod" ? "Prod" : "UAT";
  const cfg = convertConfig.paymentFirstExperiment[key];
  return {
    id: cfg.experimentId,
    name: PAYMENT_FIRST_EXPERIMENT_NAME,
    // The variant arm served at PAYMENT_FIRST_CHECKOUT_PATH is always the
    // payment-first variation; useAnalytics emits this as variant_id so it
    // joins to Convert's variation-level data.
    variant: Number(cfg.variationVariant),
    variant_name: PAYMENT_FIRST_VARIANT_NAME,
  };
};

// The arm to tag a checkout event or charge with. On the variant route the URL
// is authoritative; everywhere else AppContext is the only signal, so it stays
// the fallback (and remains the whole mechanism for any other experiment).
//
// Deliberately shared by BOTH tagging call sites — useAnalytics (client Segment
// events) and BraintreeCheckout's checkout request body (which the backend turns
// into the server-side `purchase` tag, OMG-2155). Those two are required to
// agree: if only the client side resolved from the URL, PF revenue attribution
// would keep the ~86% context-race tag loss the client funnel just shed, and the
// two sources would disagree about the same session's arm (OMG-2313).
export const resolveActiveExperiment = (
  contextExperiment: Experiment | null | undefined,
  env: string | undefined,
): Experiment | null | undefined =>
  typeof window !== "undefined" && isPaymentFirstPath(window.location.pathname)
    ? getPaymentFirstExperiment(env)
    : contextExperiment;
